import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import * as PhosphorIcons from "@phosphor-icons/react";
import { PracticeQuickViewModal, PracticeActionButton, PracticeCustomActionInlineButton } from "./PracticeQuickView";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const MONTH_SHORT = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];

/** Countdown giorni residui alla consegna stimata per una pratica del PRO.
 * - Se completata → mostra data completamento.
 * - Se cancellata → "—".
 * - Servizio senza `delivery_time_days` → "—".
 * - Servizio con visita ma sopralluogo NON ancora fissato → "In attesa sopralluogo"
 *   (countdown non partito: parte dal giorno dopo il rilievo).
 * - In corso: `-N gg` (rosso, scaduta) o `+N gg` (verde/ambra).
 *
 * Logica anchor calcolata backend (`_compute_delivery_deadline`):
 * - Con sopralluogo: parte dal giorno DOPO il rilievo (se rilievo dopo le 12:00 → +1 giorno extra)
 * - Senza sopralluogo: parte dal giorno della prenotazione (se dopo le 12:00 → indomani)
 */
export function DeliveryCountdown({ practice: p }) {
  if (p.status === "completed") {
    return p.completed_at
      ? <span className="text-slate-600">{new Date(p.completed_at).toLocaleDateString("it-IT")}</span>
      : <span className="text-slate-400">—</span>;
  }
  if (p.status === "cancelled") return <span className="text-slate-400">—</span>;
  const days = p.days_left;
  if (days === null || days === undefined) {
    // Servizio con visita non ancora fissata → countdown in stand-by
    if (p.delivery_time_days > 0) {
      return (
        <span
          className="inline-flex flex-wrap items-center gap-1 text-slate-500 text-[11px] italic"
          title="Il countdown partirà al completamento di pagamenti diritti, firme e sopralluogo"
          data-testid={`countdown-${p.id}-pending`}
        >
          In attesa
          {p.waiting_reasons?.length > 0 && (
            <span className="not-italic text-amber-700" data-testid={`countdown-${p.id}-reasons`}>
              · {p.waiting_reasons.join(" · ")}
            </span>
          )}
        </span>
      );
    }
    // Servizio senza delivery_time_days configurato
    return <span className="text-slate-400">—</span>;
  }
  const overdue = days < 0;
  const today = days === 0;
  const warning = days > 0 && days <= 2;
  const cls = overdue
    ? "text-red-600 font-bold"
    : today
      ? "text-orange-600 font-bold"
      : warning
        ? "text-amber-600 font-semibold"
        : "text-emerald-700 font-semibold";
  const label = overdue
    ? `${days} gg` // già negativo
    : today
      ? "oggi"
      : `+${days} gg`;
  return (
    <span
      className={`inline-flex items-center gap-1 ${cls}`}
      title={p.delivery_deadline ? `Consegna stimata entro il ${new Date(p.delivery_deadline).toLocaleDateString("it-IT")}` : ""}
      data-testid={`countdown-${p.id}`}
    >
      {overdue && <PhosphorIcons.WarningCircle size={12} weight="fill" />}
      {label}
    </span>
  );
}

/** Tab "Storico servizi" del Marketplace: recap mensile con tutti i servizi
 * acquistati (in lavorazione + completati). Clic su riga apre modal
 * anteprima (senza chat/documenti operativi/storico interno). */
export default function RecapTable({ recap, expandedMonth, onToggleMonth }) {
  const [previewRow, setPreviewRow] = useState(null);
  const [openCardId, setOpenCardId] = useState(null);
  const [query, setQuery] = useState("");
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();

  // Iter208.85 — Deep-link dalle notifiche "Comunicazioni": ?open_practice=<pid>
  // apre automaticamente il modal della pratica corrispondente.
  useEffect(() => {
    const pid = searchParams.get("open_practice");
    if (!pid || !recap?.length) return;
    // Cerca la pratica in tutti i mesi del recap
    for (const month of recap) {
      const found = (month.practices || []).find((p) => (p.id || p._id) === pid);
      if (found) {
        setPreviewRow(found);
        break;
      }
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open_practice");
    setSearchParams(next, { replace: true });
  }, [recap, searchParams, setSearchParams]);

  if (recap.length === 0) {
    return (
      <div className="kpi-card text-center py-16" data-testid="recap-empty">
        <PhosphorIcons.ChartLine size={48} className="mx-auto mb-4 text-muted-foreground" weight="duotone" />
        <p className="text-muted-foreground">Nessun servizio acquistato.</p>
        <p className="text-xs text-muted-foreground mt-2">
          Il riepilogo apparirà mese per mese con i servizi in lavorazione e quelli consegnati.
        </p>
      </div>
    );
  }

  /** Badge stato pratica basato su `lifecycle_status` (derivato backend).
   * - completed → verde
   * - cancelled → grigio
   * - attesa_pagamento_cliente → giallo (nuovo stato)
   * - pagato_cliente → blu (nuovo stato)
   * - fallback → arancio "In lavorazione"
   */
  const StatusBadge = ({ practice }) => {
    const lc = practice.lifecycle_status || practice.status;
    if (practice.cash_pending) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-semibold whitespace-nowrap animate-pulse"
          data-testid={`status-cash-pending-${practice.id}`}
          title="Il cliente pagherà in contanti al ritiro del documento"
        >
          <PhosphorIcons.Money size={11} weight="fill" /> Contanti da incassare
        </span>
      );
    }
    if (lc === "completed") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold whitespace-nowrap">
          ✓ Completato
        </span>
      );
    }
    if (lc === "cancelled") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold whitespace-nowrap">
          Annullato
        </span>
      );
    }
    if (lc === "attesa_pagamento_cliente") {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[10px] font-semibold whitespace-nowrap"
          data-testid={`status-attesa-${practice.id}`}
          title="In attesa che il cliente completi il pagamento"
        >
          ⏱ Attesa pagamento
        </span>
      );
    }
    if (lc === "pagato_cliente") {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold whitespace-nowrap"
          data-testid={`status-pagato-${practice.id}`}
        >
          ✓ Pagato cliente
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold whitespace-nowrap">
        ⏳ In lavorazione
      </span>
    );
  };

  /** Bottone "Copia link pagamento" visibile solo se
   * `lifecycle_status="attesa_pagamento_cliente"`. Dopo il pagamento
   * mostra un pill disabilitato "✓ Pagato". */
  const PaymentLinkCell = ({ practice: p }) => {
    if (p.cash_pending) {
      return (
        <span className="inline-flex items-center gap-1 text-amber-800 text-[10px] font-semibold whitespace-nowrap" data-testid={`payment-cash-${p.id}`}>
          <PhosphorIcons.Money size={11} /> Contanti
        </span>
      );
    }
    if (p.lifecycle_status === "attesa_pagamento_cliente") {
      const onCopy = (e) => {
        e.stopPropagation();
        const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const url = `${base}/pay/${p.id}`;
        navigator.clipboard.writeText(url).then(
          () => toast.success("Link pagamento copiato"),
          () => toast.error("Errore copia"),
        );
      };
      return (
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-yellow-300 bg-yellow-50 hover:bg-yellow-100 text-yellow-800 text-[10px] font-semibold whitespace-nowrap transition-colors"
          data-testid={`copy-payment-link-${p.id}`}
          title="Copia il link di pagamento da inviare al cliente"
        >
          <PhosphorIcons.Copy size={11} /> Copia link
        </button>
      );
    }
    if (p.lifecycle_status === "pagato_cliente" || p.payment_status === "paid") {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-500 text-[10px] font-semibold whitespace-nowrap cursor-not-allowed"
          data-testid={`payment-link-disabled-${p.id}`}
          title="Pagamento già confermato"
        >
          <PhosphorIcons.CheckCircle size={11} weight="fill" /> Pagato
        </span>
      );
    }
    return <span className="text-slate-300 text-[11px]">—</span>;
  };

  const renderPayment = (p) => (
    <>
      {p.payment_type === "plafond" ? (
        <span className="inline-block px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-[10px] font-semibold">
          💰 Plafond
        </span>
      ) : (
        <PaymentLinkCell practice={p} />
      )}
      {p.payment_status === "paid" && (p.payment_type === "plafond" || p.payment_type === "client") && (
        <div className="text-xs text-muted-foreground mt-1" data-testid={`recap-paid-note-${p.id}`}>
          {p.payment_type === "plafond" ? "Pagato con plafond" : "Pagato dal cliente"}
        </div>
      )}
    </>
  );

  const renderMargin = (p) => (p.payment_type === "client" ? (
    <div className="flex flex-col items-end leading-tight">
      {p.margin < 0 ? (
        <span
          className="text-red-600 font-semibold"
          data-testid={`recap-margin-negative-${p.id}`}
          title="Vendita sottocosto: la differenza è stata scalata dal tuo plafond"
        >
          {fmt(p.margin)}
        </span>
      ) : (
        <span className="text-emerald-700 font-semibold">+{fmt(p.margin)}</span>
      )}
      {p.sub_user_name && p.sub_margin_amount > 0 && (
        <span
          className="text-[10px] text-purple-700 font-normal"
          title={`Ricarico applicato da ${p.sub_user_name}`}
          data-testid={`recap-submargin-${p.id}`}
        >
          di cui +{fmt(p.sub_margin_amount)}
          {p.sub_margin_pct ? ` (${p.sub_margin_pct}%)` : ""}
        </span>
      )}
    </div>
  ) : (
    <span className="text-slate-400">—</span>
  ));

  // Mobile: card accordion (una sola aperta alla volta).
  const renderMobileCard = (p) => {
    const open = openCardId === p.id;
    const toggle = () => setOpenCardId((cur) => (cur === p.id ? null : p.id));
    const field = (label, content, testid) => (
      <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-b-0" data-testid={testid}>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className="text-right text-xs">{content}</div>
      </div>
    );
    return (
      <tr key={p.id} data-testid={`recap-card-${p.id}`} className={`!p-0 ${p.cash_pending ? "ring-2 ring-amber-400" : ""}`}>
        <td className="!block !p-0 !text-left">
          <div
            role="button"
            tabIndex={0}
            onClick={toggle}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
            className="px-3.5 py-3 cursor-pointer"
            aria-expanded={open}
            data-testid={`recap-card-toggle-${p.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold text-sm text-slate-900 min-w-0">{p.service_name}</div>
              <div className="flex items-center gap-1.5 shrink-0">
                <StatusBadge practice={p} />
                <PhosphorIcons.CaretDown size={14} className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
              </div>
            </div>
            <div className="mt-1 text-xs text-muted-foreground truncate">
              {[p.client, p.address].filter(Boolean).join(" | ")}
            </div>
            {!open && (
              <div className="mt-1.5 flex justify-start" onClick={(e) => e.stopPropagation()}>
                <div className="inline-flex items-center gap-1.5 [&_button]:!h-auto [&_button]:!py-0 [&_button]:!px-0 [&_button]:!border-0 [&_button]:!bg-transparent [&_button]:!shadow-none [&_button]:!text-[11px] [&_button]:!text-purple-700 [&_button]:underline [&_svg]:!w-3 [&_svg]:!h-3" data-testid={`recap-card-action-link-${p.id}`}>
                  <PracticeCustomActionInlineButton practice={p} />
                  <PracticeActionButton practice={p} compact />
                </div>
              </div>
            )}
          </div>
          {open && (
            <div className="px-3.5 pb-3 border-t border-slate-100" data-testid={`recap-card-details-${p.id}`}>
              {field("Team/Collaboratore", p.sub_user_name || <span className="text-slate-400">Tu</span>, `recap-card-team-${p.id}`)}
              {field("Prezzo", (
                <div className="font-mono">
                  <div className="font-semibold text-slate-900">{fmt(p.professional_price)} <span className="font-sans text-[10px] text-slate-500">+ IVA</span></div>
                  <div className="text-[10px] text-slate-400">tot {fmt(p.total_price)}</div>
                </div>
              ), `recap-card-price-${p.id}`)}
              {field("Consegna", <span className="font-mono"><DeliveryCountdown practice={p} /></span>)}
              {field("Pagamento", renderPayment(p))}
              {field("Margine", <span className="font-mono">{renderMargin(p)}</span>)}
              {p.credit_matured > 0 && field("Credito", <span className="font-mono text-emerald-700">+{fmt(p.credit_matured)}</span>)}
              <div className="mt-3 space-y-2 [&_div]:!w-full [&_div]:!items-stretch [&_button]:!w-full [&_button]:justify-center" onClick={(e) => e.stopPropagation()} data-testid={`recap-card-actions-${p.id}`}>
                <PracticeCustomActionInlineButton practice={p} />
                <PracticeActionButton practice={p} />
              </div>
              <button
                type="button"
                onClick={() => setPreviewRow(p)}
                className="mt-2 w-full text-center text-xs font-semibold text-purple-700 hover:underline"
                data-testid={`recap-card-open-${p.id}`}
              >
                Apri cartella del servizio
              </button>
            </div>
          )}
        </td>
      </tr>
    );
  };


  const q = query.trim().toLowerCase();
  const visibleRecap = q
    ? recap
        .map((m) => ({
          ...m,
          practices: m.practices.filter((p) =>
            `${p.client || ""} ${p.address || ""}`.toLowerCase().includes(q),
          ),
        }))
        .filter((m) => m.practices.length > 0)
    : recap;

  return (
    <div className="space-y-3" data-testid="recap-list">
      <div className="relative" data-testid="recap-search">
        <PhosphorIcons.MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca per cliente o indirizzo…"
          className="w-full h-10 rounded-md border bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
          data-testid="recap-search-input"
        />
      </div>
      {q && visibleRecap.length === 0 && (
        <div className="kpi-card text-sm text-muted-foreground text-center" data-testid="recap-search-empty">
          Nessuna pratica trovata per “{query.trim()}”.
        </div>
      )}
      {visibleRecap.map((m) => {
        const key = `${m.year}-${String(m.month).padStart(2, "0")}`;
        const expanded = q ? true : expandedMonth === key;
        const completedCount = m.practices.filter((p) => p.status === "completed").length;
        const inProgressCount = m.practices.filter(
          (p) => !["completed", "cancelled"].includes(p.status),
        ).length;
        return (
          <div key={key} className="kpi-card p-0 overflow-hidden" data-testid={`recap-month-${key}`}>
            <button
              type="button"
              onClick={() => onToggleMonth(expanded ? null : key)}
              className="w-full flex items-center justify-between gap-4 p-4 hover:bg-slate-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md ht-gradient text-white font-bold flex flex-col items-center justify-center leading-none">
                  <div className="text-[9px] uppercase tracking-widest">{MONTH_SHORT[m.month - 1]}</div>
                  <div className="text-sm">{m.year}</div>
                </div>
                <div>
                  <div className="font-semibold">{m.label}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span>{m.practices.length} {m.practices.length === 1 ? "servizio" : "servizi"}</span>
                    {inProgressCount > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-semibold">
                        {inProgressCount} in lavorazione
                      </span>
                    )}
                    {completedCount > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px] font-semibold">
                        {completedCount} completati
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-4 text-xs">
                <div className="text-right">
                  <div className="text-muted-foreground uppercase tracking-wider text-[9px] font-bold">Plafond</div>
                  <div className="font-mono font-semibold">{fmt(m.plafond_used)}</div>
                </div>
                <div className="text-right">
                  <div className="text-muted-foreground uppercase tracking-wider text-[9px] font-bold">Credito</div>
                  <div className={`font-mono font-semibold ${m.credit_earned < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmt(m.credit_earned)}</div>
                </div>
                <div className="text-right border-l pl-4">
                  <div className="text-muted-foreground uppercase tracking-wider text-[9px] font-bold">
                    Dovuto Housetag
                  </div>
                  <div className={`font-mono font-bold ${m.net_due >= 0 ? "text-purple-800" : "text-emerald-800"}`}>
                    {fmt(m.net_due)}
                  </div>
                </div>
              </div>
              <PhosphorIcons.CaretDown
                size={16}
                className={`text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </button>

            {expanded && (
              <div className="border-t bg-slate-50/40" data-testid={`recap-detail-${key}`}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs mobile-cards">
                    <thead className="bg-white border-b">
                      <tr className="text-left">
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600">Stato</th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600">Servizio</th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600">Indirizzo</th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600">Team/Collaboratore</th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600">Cliente</th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600 text-right">Prezzo</th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600">Consegna</th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600">Pagamento</th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600 text-right">
                          Credito
                        </th>
                        <th className="p-3 font-bold text-[10px] uppercase tracking-wider text-slate-600 text-right">
                          Azione
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Iter88: raggruppa le righe che condividono visit_group_id.
                        // Le pratiche standalone (no visit_group_id) restano righe singole.
                        // I gruppi vengono resi con sfondo/bordo leggermente diverso e le
                        // pratiche chained mostrano un'icona di collegamento a curva.
                        const groups = new Map();
                        m.practices.forEach((p) => {
                          if (p.visit_group_id) {
                            if (!groups.has(p.visit_group_id)) groups.set(p.visit_group_id, []);
                            groups.get(p.visit_group_id).push(p);
                          }
                        });
                        return m.practices.map((p) => {
                          const gid = p.visit_group_id;
                          const grouped = gid && groups.get(gid)?.length > 1;
                          const isChained = grouped && p.visit_group_role === "chained";
                          const isPrimary = grouped && p.visit_group_role === "primary";
                          // Iter209bu — Nella lista mostriamo SOLO la pratica
                          // madre (primary). I figli chained restano visibili
                          // dentro il modal di dettaglio nella sezione
                          // "Servizi concatenati".
                          if (isChained) return null;
                          // Trova la posizione della riga all'interno del gruppo per
                          // dare bordi diversi (prima → arrotonda sopra, ultima → sotto)
                          const gRows = grouped ? groups.get(gid) : [];
                          const gIdx = grouped ? gRows.indexOf(p) : -1;
                          const isFirstInGroup = grouped && gIdx === 0;
                          const isLastInGroup = grouped && gIdx === gRows.length - 1;
                          const groupCls = grouped
                            ? `bg-purple-50/60 border-l-4 border-l-purple-400 ${
                                isFirstInGroup ? "" : "border-t-0"
                              } ${isLastInGroup ? "" : "border-b-transparent"}`
                            : "";
                          if (isMobile) return renderMobileCard(p);
                          return (
                        <tr
                          key={p.id}
                          className={`border-b last:border-b-0 hover:bg-purple-50/60 cursor-pointer transition-colors ${groupCls} ${p.cash_pending ? "ring-2 ring-inset ring-amber-400" : ""}`}
                          onClick={() => setPreviewRow(p)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setPreviewRow(p);
                            }
                          }}
                          data-testid={`recap-row-${p.id}`}
                          data-group-role={isPrimary ? "primary" : isChained ? "chained" : undefined}
                          title="Clicca per vedere lo stato e le info del servizio"
                        >
                          <td data-label="Stato" className="p-3">
                            <StatusBadge practice={p} />
                          </td>
                          <td data-label="Servizio" className="p-3 font-medium">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {isChained && (
                                <PhosphorIcons.ArrowBendDownRight
                                  size={14}
                                  weight="bold"
                                  className="text-purple-500 flex-shrink-0"
                                  title="Servizio collegato al sopralluogo principale"
                                  data-testid={`recap-chained-icon-${p.id}`}
                                />
                              )}
                              {isPrimary && (
                                <PhosphorIcons.LinkSimple
                                  size={12}
                                  weight="bold"
                                  className="text-purple-600 flex-shrink-0"
                                  title="Sopralluogo condiviso con altri servizi"
                                  data-testid={`recap-primary-icon-${p.id}`}
                                />
                              )}
                              <span>{p.service_name}</span>
                              {p.can_reschedule_visit && (
                                <span
                                  className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded"
                                  title="Sopralluogo modificabile (fino a 12h prima dell'appuntamento)"
                                  data-testid={`can-reschedule-badge-${p.id}`}
                                >
                                  <PhosphorIcons.CalendarBlank size={9} weight="fill" />
                                  Modificabile
                                </span>
                              )}
                            </div>
                          </td>
                          <td data-label="Indirizzo" className="p-3 text-muted-foreground">
                            {p.address}
                          </td>
                          <td data-label="Team/Collaboratore" className="p-3" data-testid={`recap-team-${p.id}`}>
                            {p.sub_user_name ? (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-800 text-[11px] font-medium"
                                title="Gestita da un collaboratore del team"
                              >
                                <PhosphorIcons.Users size={11} weight="fill" />
                                {p.sub_user_name}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-[11px]" title="Gestita direttamente dal Master">
                                Tu
                              </span>
                            )}
                          </td>
                          <td data-label="Cliente" className="p-3">
                            {p.client}
                          </td>
                          <td data-label="Prezzo" className="p-3 text-right font-mono" data-testid={`recap-price-${p.id}`}>
                            <div className="text-slate-900 font-semibold whitespace-nowrap">{fmt(p.professional_price)}<span className="ml-1 text-[10px] font-sans font-normal text-slate-500" data-testid={`recap-price-iva-${p.id}`}>+ IVA</span></div>
                            <div className="text-[10px] text-slate-400 leading-tight">tot {fmt(p.total_price)}</div>
                          </td>
                          <td data-label="Consegna" className="p-3 font-mono" data-testid={`recap-delivery-${p.id}`}>
                            <DeliveryCountdown practice={p} />
                          </td>
                          <td data-label="Pagamento" className="p-3">
                            {renderPayment(p)}
                          </td>
                          <td data-label="Margine" className="p-3 text-right font-mono">
                            {renderMargin(p)}
                          </td>
                          <td data-label="Azione" className="p-3 text-right whitespace-nowrap"
                              onClick={(e) => e.stopPropagation()}>
                            {/* Iter88: nascondi bottoni azione sulle righe chained per evitare
                                doppioni — il PRO agisce solo sulla riga primary del gruppo. */}
                            {isChained ? (
                              <span
                                className="text-[10px] text-slate-400 italic"
                                title="Le azioni sono gestite dal servizio principale del gruppo"
                                data-testid={`recap-chained-noaction-${p.id}`}
                              >
                                — via principale
                              </span>
                            ) : (
                              <div className="inline-flex items-center gap-1.5 justify-end">
                                <PracticeCustomActionInlineButton practice={p} />
                                <PracticeActionButton practice={p} compact />
                              </div>
                            )}
                          </td>
                        </tr>
                          );
                        });
                      })()}
                    </tbody>
                    <tfoot className="bg-white border-t">
                      <tr className="text-[11px]">
                        <td colSpan="7" className="p-3 text-right font-bold uppercase tracking-wider text-slate-700">
                          Totali del mese
                        </td>
                        <td className="p-3 text-xs">
                          <div>
                            Plafond utilizzato:{" "}
                            <span className="font-mono font-semibold text-purple-800">{fmt(m.plafond_used)}</span>
                          </div>
                          <div>
                            Credito maturato:{" "}
                            <span className={`font-mono font-semibold ${m.credit_earned < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmt(m.credit_earned)}</span>
                          </div>
                          {m.practices.some((p) => p.sub_user_name) && (
                            <div
                              className="text-[10px] text-muted-foreground italic mt-1 flex items-center gap-1"
                              data-testid={`recap-team-note-${m.year}-${m.month}`}
                            >
                              <PhosphorIcons.Info size={10} weight="fill" className="text-purple-500" />
                              Il credito maturato include i margini generati da tutto il team.
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">
                            Dovuto Housetag
                          </div>
                          <div
                            className={`font-mono font-bold text-base ${m.net_due >= 0 ? "text-purple-800" : "text-emerald-800"}`}
                          >
                            {fmt(m.net_due)}
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Modal anteprima servizio (sostituisce la navigazione al PracticeDetail) */}
      <PracticeQuickViewModal
        open={!!previewRow}
        onClose={() => setPreviewRow(null)}
        practiceRow={previewRow}
      />
    </div>
  );
}
